import { DUMMY_ROOT_VALUE } from 'business/constants';

import { TreeWithDisplayOptionsT, TreeWithOptionalIsDisabled } from './types';

/**
 * Recursively processes a tree to determine which nodes should be displayed, selectable, and expanded based on a search function.
 *
 * This function traverses the tree and:
 * - Filters nodes according to a search predicate.
 * - Marks nodes as bold if they match the search.
 * - Determines which node values are selectable (matching the search or having a selectable parent).
 * - Determines which node values should be expanded (matching the search or having expanded descendants).
 *
 * @param {TreeWithOptionalIsDisabled} tree
 *   The tree node to process.
 * @param {(nodeLabel: string, trueIfEmptySearch?: boolean) => boolean} isNodeMatchingSearch
 *   A function that returns true if a node label matches the search criteria.
 * @param {boolean} isParentSelectable boolean (optional, default: false)
 *   Indicates if the parent node is selectable.
 * @returns {
 *   processedTree: TreeWithDisplayOptionsT | null;
 *   selectableValues: string[];
 *   expandedValues: string[];
 * }
 *   An object containing:
 *     - processedTree: The filtered and decorated tree for display, or null if the node and its children do not match.
 *     - selectableValues: An array of node values that are selectable.
 *     - expandedValues: An array of node values that should be expanded to reveal matches.
 */

export const getDisplayableTreeAndSelectableValuesAndExpandedValues = (
    tree: TreeWithOptionalIsDisabled,
    isNodeMatchingSearch: (
        nodeLabel: string,
        trueIfEmptySearch?: boolean,
    ) => boolean,
    isParentSelectable: boolean = false,
): {
    processedTree: TreeWithDisplayOptionsT | null;
    selectableValues: string[];
    expandedValues: string[];
} => {
    const newChildren: TreeWithDisplayOptionsT[] = [];
    const newExpandedValues: string[] = [];

    const nodeMatchingSearch = isNodeMatchingSearch(tree.label, false);

    // Node is selectable if it matches the search or if one of its parents is selectable
    const selectableValues: string[] =
        (isNodeMatchingSearch(tree.label) || isParentSelectable) &&
        tree.value !== DUMMY_ROOT_VALUE
            ? [tree.value]
            : [];

    tree.children.forEach((child) => {
        const {
            processedTree,
            selectableValues: childSelectableValues,
            expandedValues: childExpandedValues,
        } = getDisplayableTreeAndSelectableValuesAndExpandedValues(
            child,
            isNodeMatchingSearch,
            isParentSelectable || nodeMatchingSearch,
        );
        if (processedTree) {
            newChildren.push(processedTree);
        }
        selectableValues.push(...childSelectableValues);
        newExpandedValues.push(...childExpandedValues);
    });

    // If no children are kept, the node itself doesn't match the search and its parent is not selectable, we don't display the node
    if (
        newChildren.length === 0 &&
        !isNodeMatchingSearch(tree.label) &&
        !isParentSelectable
    ) {
        return {
            processedTree: null,
            selectableValues: [],
            expandedValues: [],
        };
    }

    //the node is expanded if it matches the search or if it has at least a child that is expanded
    const isExpanded =
        isNodeMatchingSearch(tree.label, false) || newExpandedValues.length > 0;

    return {
        processedTree: {
            ...tree,
            children: newChildren,
            isBold: nodeMatchingSearch,
            isDisabled: tree.isDisabled,
        },
        selectableValues: selectableValues,
        expandedValues: isExpanded
            ? [tree.value, ...newExpandedValues]
            : newExpandedValues,
    };
};

export const removeAccents = (str: string) =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const keepOnlyAlphaAndDigitCharacters = (str: string) =>
    // eslint-disable-next-line no-useless-escape
    str.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, '');

const normalizeString = (str: string) =>
    keepOnlyAlphaAndDigitCharacters(removeAccents(str));

export const getCaseInsensitiveRegex = (pattern: string) =>
    new RegExp(pattern, 'i');

export const getNormalizedAndCaseInsensitiveRegex = (
    text: string | undefined,
): RegExp => {
    const normalizedSearchedText = normalizeString(text || '');
    const regexPattern = getCaseInsensitiveRegex(normalizedSearchedText);
    return regexPattern;
};

export function matchIgnoringCaseAndAccents(
    label: string | undefined,
    normalizedAndCaseInsensitiveRegex: RegExp,
) {
    const normalizedLabel = normalizeString(label || '');
    return normalizedAndCaseInsensitiveRegex.test(normalizedLabel);
}

export const removeElementsFromSelection = (
    selection: string[],
    elementsToRemove: string[],
) => selection.filter((node) => !elementsToRemove.includes(node));

export const addElementsToSelection = (
    selection: string[],
    elementsToAdd: string[],
): string[] => Array.from(new Set([...selection, ...elementsToAdd]));

export const getNodeAndItsDescendantsValues = (node: TreeT): string[] => {
    const childrenNodeAndTheirDescendansIds = node.children
        .map((child) => getNodeAndItsDescendantsValues(child))
        .flat();
    return [node.value, ...childrenNodeAndTheirDescendansIds];
};

/**
 * Returns a predicate function to check if a tree node label matches a search text,
 * ignoring case and accents.
 *
 * @param {string} text The search text to match against node labels.
 * @returns A function that takes a node label and an optional boolean (trueIfEmptySearch)
 *          and returns true if the label matches the search text, false otherwise.
 */
export const getIsNodeMatchingSearch =
    (text: string) =>
    (nodeLabel: string, trueIfEmptySearch: boolean = true): boolean => {
        if (text === '') return trueIfEmptySearch;

        return matchIgnoringCaseAndAccents(
            nodeLabel,
            getNormalizedAndCaseInsensitiveRegex(text),
        );
    };
